import React from 'react';
import { Stack, Text } from '@chaser/components';
import { Shelf } from '@chaser/components';

/**
 * An error details.
 *
 * @param {Object} props - An object containing error information.
 * @param {string} [props.message] - A brief/introductory error message.
 * @param {Object[]} [props.rejectedFiles] - An array of rejected files.
 * @param {string} [props.rejectedFiles[].name] - The name of the rejected file. Either `name` (preffered) or `file` is required.
 * @param {File} [props.rejectedFiles[].file] - The rejected file. Either `name` or `file` is required.
 * @param {string|Error[]} [props.rejectedFiles[].errors] - A single error message or an array of Errors for the rejected file.
 * @param {boolean} [props.rejectedFiles[].hideName] - Whether to hide the name of the rejected file.
 * @param {string} [props.summary] - A summary of the error. Will be displayed in bold after detailed error information is shown.
 *
 * @return {JSX.Element} A JSX element representing the error details.
 */
const FileUploadErrorsList = ({ message, rejectedFiles, summary }) => {
  const hasIndividualErrors = rejectedFiles?.some(
    ({ errors }) => errors && (typeof errors === 'string' || Array.isArray(errors)),
  );

  return (
    <Stack gap="medium">
      {message && <Text color="inherit">{message}</Text>}
      {rejectedFiles?.length > 0 &&
        (hasIndividualErrors ? (
          <Stack gap="xsmall" alignItems="center">
            {rejectedFiles.map(({ name, file, errors, hideName }) => {
              let error = 'Unknown error';
              if (Array.isArray(errors)) {
                if (errors.length === 1) {
                  error = errors[0].message;
                } else {
                  error = (
                    <Stack gap="xsmall">
                      {errors.map(e => (
                        <Text key={e.message} color="inherit">
                          {e.message}
                        </Text>
                      ))}
                    </Stack>
                  );
                }
              } else if (typeof errors === 'string') {
                error = errors;
              }

              const fileName = name ? name : typeof file === 'string' ? file : file.name;

              return (
                <Shelf gap="xsmall" key={fileName} alignItems="flex-start">
                  {hideName ? null : <Text color="inherit">{fileName}:</Text>}
                  {typeof error === 'string' ? <Text color="inherit">{error}</Text> : error}
                </Shelf>
              );
            })}
          </Stack>
        ) : (
          <Text color="inherit">
            {rejectedFiles
              .map(({ name, file }) => (name ? name : typeof file === 'string' ? file : file.name))
              .join(', ')}
          </Text>
        ))}
      {summary && (
        <Text color="inherit" fontWeight="bold">
          {summary}
        </Text>
      )}
    </Stack>
  );
};

export default FileUploadErrorsList;
